import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createLogger } from '@/lib/logger';
import {
  Animated,
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  Platform,
  Image,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import TutorialOverlay from '@/components/TutorialOverlay';
import { useRouter, useLocalSearchParams, useFocusEffect } from 'expo-router';
import SkeletonRow from '@/components/SkeletonRow';
import ActivityHeatmap from '@/components/ActivityHeatmap';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileStore } from '@/stores/useProfileStore';
import { supabase } from '@/lib/supabase';
import {
  Settings,
  Trophy,
  Activity,
  Award,
  LogOut,
  Crown,
  Edit,
  Users,
  Bell,
  ChevronRight,
  Share2,
  Camera,
  Swords,
  Flame,
  Calendar,
  MapPin,
} from 'lucide-react-native';
import { DuelWinLossRecord } from '@/components/DuelWinLossRecord';
import RankTierInfo, { RankTierInfoButton } from '@/components/RankTierInfo';
import { useTheme } from '@/contexts/ThemeContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast } from '@/lib/subscription';
import { isFlagEnabled } from '@/lib/featureFlags';
import { handleToUsername } from '@/lib/types/publicProfile';
import { V1_LIFT_LABELS, type V1Lift } from '@/lib/lifts/canonical';
import {
  getTrainerLinkStatus,
  getCtaForStatus,
  requestTrainerByProfileId,
  TrainerLinkStatus,
} from '@/lib/trainers';
import { generateProfileShareLink } from '@/lib/shareLinks';
import * as ImagePicker from 'expo-image-picker';
import AchievementBadge from '@/components/AchievementBadge';
import BadgeGrid from '@/components/BadgeGrid';
import EloRatingCard from '@/components/EloRatingCard';
import CareerStatsCard from '@/components/CareerStatsCard';
import PRTimeline from '@/components/PRTimeline';
import { evaluateAchievements, type AchievementState } from '@/lib/achievements';
import FollowButton from '@/components/FollowButton';
import FounderBadge from '@/components/FounderBadge';
import { isFounderSystemVisible } from '@/lib/founder/isFounderSystemVisible';
import { isFoundingAthlete } from '@/lib/founder/isFoundingAthlete';
import { computeFounderScore } from '@/lib/founder/computeFounderScore';
import { computeSummitStatus } from '@/lib/founder/computeSummitStatus';
import SummitProgressCard from '@/components/founder/SummitProgressCard';
import BlockReportModal from '@/components/BlockReportModal';
import { MoreHorizontal } from 'lucide-react-native';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, TRACKING, SPACE, CARD, SECTION_LABEL, SECTION_TITLE } from '@/constants/designTokens';
import { useUnits } from '@/lib/useUnits';

const log = createLogger('PROFILE');

const CORE_LIFTS: { key: string; label: string }[] = [
  { key: 'bench-press', label: 'Bench Press' },
  { key: 'back-squat', label: 'Back Squat' },
  { key: 'deadlift', label: 'Deadlift' },
  { key: 'weighted-pull-up', label: 'Pull-Ups' },
  { key: 'dead-hang', label: 'Dead Hang' },
];

export default function ProfileTab() {
  const router = useRouter();
  const { userId } = useLocalSearchParams();
  const { signOut, user, refreshProfile } = useAuth();
  const currentUserProfile = useProfileStore((s) => s.profile);
  const refetchInFlight = useRef(false);
  const lastRefetchAt = useRef<number | null>(null);
  const { theme } = useTheme();
  const { tier } = useSubscription();
  const { userUnits, displayWeight } = useUnits();
  const c = theme.colors;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [viewedProfile, setViewedProfile] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isVerifiedTrainer, setIsVerifiedTrainer] = useState(false);
  const [trainerLinkStatus, setTrainerLinkStatus] = useState<TrainerLinkStatus>('none');
  const [loadingTrainerInfo, setLoadingTrainerInfo] = useState(false);
  const [showRankTierInfo, setShowRankTierInfo] = useState(false);
  const [workoutDates, setWorkoutDates] = useState<string[]>([]);
  const [topPRs, setTopPRs] = useState<{ lift: string; liftKey: string; weight: number; reps: number; unit: string; date: string }[]>([]);
  const [totalWorkouts, setTotalWorkouts] = useState(0);
  const [lifetimeVolume, setLifetimeVolume] = useState(0);
  const [memberSince, setMemberSince] = useState<string | null>(null);
  const [achievements, setAchievements] = useState<AchievementState[]>([]);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [userGyms, setUserGyms] = useState<{ gym_id: string; gym_name: string }[]>([]);
  const [showBlockReport, setShowBlockReport] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fadeAnim, { toValue: 1, duration: 300, useNativeDriver: true }).start();
  }, []);

  const isViewingOtherUser = Boolean(userId && userId !== currentUserProfile?.id);
  const profile = isViewingOtherUser ? viewedProfile : currentUserProfile;

  const displayName = profile?.display_name?.trim() || profile?.handle?.trim() || user?.email?.split('@')[0] || 'User';
  const initial = (displayName?.[0] || 'U').toUpperCase();
  const handle = profile?.handle || user?.email?.split('@')[0] || 'user';
  const bio = profile?.bio?.trim();

  // Parse DUEL Standard components
  const duelComp = useMemo(() => {
    let comp: any = profile?.duel_standard_components ?? null;
    try { if (typeof comp === 'string') comp = JSON.parse(comp); } catch (e) { console.warn('Failed to parse duel_standard_components:', e); comp = null; }
    return comp;
  }, [profile?.duel_standard_components]);

  const duelScore = Math.round(profile?.duel_standard_strength_kg || 0);
  const hasFullBaseline = CORE_LIFTS.filter(l => duelComp && duelComp[l.key]).length === CORE_LIFTS.length && duelScore > 0;

  // ── Data loading ──
  useEffect(() => {
    if (isViewingOtherUser) {
      loadUserProfile();
      loadTrainerInfo();
      if (userId && typeof userId === 'string') {
        evaluateAchievements(userId).then(setAchievements).catch(() => {});
        loadUserGyms(userId);
      }
    }
  }, [userId]);

  useEffect(() => {
    if (!isViewingOtherUser && currentUserProfile) {
      loadUnreadCount();
      loadWorkoutHistory();
      loadPRs();
      loadAchievements();
      loadUserGyms(currentUserProfile.id);
    }
  }, [isViewingOtherUser, currentUserProfile]);

  useFocusEffect(
    useCallback(() => {
      if (isViewingOtherUser) return;
      (async () => {
        try {
          if (!refreshProfile || refetchInFlight.current) return;
          const now = Date.now();
          if (lastRefetchAt.current && now - lastRefetchAt.current < 3000) return;
          refetchInFlight.current = true;
          await refreshProfile();
          lastRefetchAt.current = Date.now();
        } catch (e) {
          log.error('Profile refresh failed', e);
        } finally {
          refetchInFlight.current = false;
        }
      })();
    }, [isViewingOtherUser, refreshProfile])
  );

  const loadUnreadCount = async () => {
    if (!currentUserProfile) return;
    const { count } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', currentUserProfile.id)
      .eq('is_read', false);
    setUnreadCount(count || 0);
  };

  const loadWorkoutHistory = async () => {
    if (!currentUserProfile?.id) return;
    const { data, count } = await supabase
      .from('workouts')
      .select('performed_at', { count: 'exact' })
      .eq('user_id', currentUserProfile.id)
      .order('performed_at', { ascending: false })
      .limit(500);
    setWorkoutDates((data || []).map(w => w.performed_at));
    setTotalWorkouts(count || 0);
    // Compute lifetime volume from payloads
    const { data: volData } = await supabase
      .from('workouts')
      .select('payload')
      .eq('user_id', currentUserProfile.id);
    let vol = 0;
    (volData || []).forEach((w: any) => {
      try {
        const payload = typeof w.payload === 'string' ? JSON.parse(w.payload) : w.payload;
        (payload?.exercises ?? []).forEach((ex: any) => {
          (ex.sets ?? ex.setDetails ?? []).forEach((s: any) => {
            vol += (Number(s.weight) || 0) * (Number(s.reps) || 1);
          });
        });
      } catch {}
    });
    setLifetimeVolume(Math.round(vol));
    if (currentUserProfile.created_at) {
      setMemberSince(new Date(currentUserProfile.created_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }));
    }
  };

  const loadPRs = async () => {
    if (!currentUserProfile?.id) return;
    // Get best lifts from strength_events for V1 lifts
    try {
      const { data } = await supabase
        .from('strength_events')
        .select('lift_key, weight, reps, weight_unit, performed_at')
        .eq('profile_id', currentUserProfile.id)
        .order('weight', { ascending: false })
        .limit(100);

      if (!data) return;
      const bestByLift: Record<string, typeof data[0]> = {};
      for (const row of data) {
        if (!bestByLift[row.lift_key] || row.weight > bestByLift[row.lift_key].weight) {
          bestByLift[row.lift_key] = row;
        }
      }
      setTopPRs(
        Object.values(bestByLift)
          .sort((a, b) => b.weight - a.weight)
          .slice(0, 5)
          .map(r => ({
            lift: V1_LIFT_LABELS[r.lift_key as V1Lift] || r.lift_key,
            liftKey: r.lift_key,
            weight: r.weight,
            reps: r.reps,
            unit: r.weight_unit || 'lb',
            date: new Date(r.performed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          }))
      );
    } catch { /* non-critical */ }
  };

  const loadAchievements = async () => {
    if (!currentUserProfile?.id) return;
    try {
      const result = await evaluateAchievements(currentUserProfile.id);
      setAchievements(result);
    } catch { /* non-critical */ }
  };

  const loadUserGyms = async (profileId: string) => {
    try {
      const { data } = await supabase
        .from('gym_memberships')
        .select('gym_id, status, gyms(name)')
        .eq('user_id', profileId)
        .eq('status', 'active');
      if (data) {
        setUserGyms(data.map((m: any) => ({
          gym_id: m.gym_id,
          gym_name: m.gyms?.name ?? 'Unknown Gym',
        })));
      }
    } catch { /* non-critical */ }
  };

  const handleAvatarUpload = async () => {
    if (isViewingOtherUser || !profile?.id) return;
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.9,
      });
      if (result.canceled || !result.assets?.[0]) return;
      setAvatarUploading(true);
      const asset = result.assets[0];

      // Handle MIME type and extension properly (especially for web blob URIs)
      let fileExt = 'jpg';
      let contentType = 'image/jpeg';
      if (asset.mimeType) {
        contentType = asset.mimeType;
        fileExt = asset.mimeType.split('/')[1] || 'jpg';
      } else if (!asset.uri.startsWith('blob:') && !asset.uri.startsWith('data:')) {
        const uriExt = asset.uri.split('.').pop()?.toLowerCase();
        if (uriExt && ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(uriExt)) {
          fileExt = uriExt;
          contentType = `image/${uriExt === 'jpg' ? 'jpeg' : uriExt}`;
        }
      }
      const filePath = `${profile.id}/avatar.${fileExt}`;

      // Use expo-file-system on native: fetch().blob() returns empty blobs on some iOS/Android versions
      let fileBody: Blob | Uint8Array;
      if (Platform.OS !== 'web') {
        const FileSystem = await import('expo-file-system');
        const b64 = await FileSystem.readAsStringAsync(asset.uri, { encoding: 'base64' as any });
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        fileBody = bytes;
      } else {
        const response = await fetch(asset.uri);
        fileBody = await response.blob();
      }

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, fileBody, { upsert: true, contentType });
      if (uploadError) { Alert.alert('Upload Failed', 'Couldn\'t upload your photo. Please try again.'); return; }
      const { data: publicUrlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
      const publicUrl = `${publicUrlData.publicUrl}?t=${Date.now()}`;
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', profile.id);
      if (updateError) { Alert.alert('Update Failed', 'Couldn\'t update your profile. Please try again.'); return; }
      // force=true bypasses the 2s debounce in useProfileStore so the new avatar_url is read immediately
      if (refreshProfile) await refreshProfile(true);
    } catch (e: any) {
      Alert.alert('Something went wrong', 'Couldn\'t upload your photo. Please try again.');
    } finally {
      setAvatarUploading(false);
    }
  };

  const loadUserProfile = async () => {
    if (!userId || typeof userId !== 'string') return;
    setLoadingProfile(true);
    const { data } = await supabase
      .from('profiles')
      .select('id, handle, display_name, gym_id, home_gym_id, home_gym_name, avatar_url, duel_standard_strength_kg, duel_standard_components, duel_standard_updated_at, bodyweight, gender, created_at, bio, current_rank, lifting_score, callus_score, badges_count, units')
      .eq('id', userId)
      .single();
    if (data) {
      setViewedProfile({
        ...(data as any),
        username: handleToUsername((data as any).handle ?? null),
        training_tags: [],
        home_gym_name: null, home_gym_address: null,
        trial_ends_at: null,
      });
      // Load workout dates for the viewed user's heatmap
      const { data: workoutData, count } = await supabase
        .from('workouts')
        .select('performed_at', { count: 'exact' })
        .eq('user_id', userId)
        .order('performed_at', { ascending: false })
        .limit(500);
      setWorkoutDates((workoutData || []).map((w: any) => w.performed_at));
      setTotalWorkouts(count || 0);
      if ((data as any).created_at) {
        setMemberSince(new Date((data as any).created_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }));
      }
    }
    setLoadingProfile(false);
  };

  const loadTrainerInfo = async () => {
    if (!isFlagEnabled('TRAINERS_ENABLED') || !userId || typeof userId !== 'string') return;
    setLoadingTrainerInfo(true);
    try {
      const { data: tp } = await supabase
        .from('trainer_profiles')
        .select('verification_status')
        .eq('trainer_profile_id', userId)
        .maybeSingle();
      if (tp?.verification_status === 'verified') {
        setIsVerifiedTrainer(true);
        setTrainerLinkStatus(await getTrainerLinkStatus(userId));
      }
    } catch { /* */ }
    finally { setLoadingTrainerInfo(false); }
  };

  const handleRequestTrainer = async () => {
    if (!userId || typeof userId !== 'string' || loading) return;
    setLoading(true);
    try {
      const result = await requestTrainerByProfileId(userId);
      if (!result.ok) {
        Alert.alert('Something went wrong', 'Couldn\'t send your request. Please try again.');
        return;
      }
      Alert.alert('Sent', 'Trainer Request Sent');
      setTrainerLinkStatus('pending');
    } catch {
      Alert.alert('Something went wrong', 'Couldn\'t send your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    if (isViewingOtherUser) return;
    if (Platform.OS === 'web') {
      if (!window.confirm('Are you sure you want to sign out?')) return;
      setLoading(true);
      await signOut();
      setLoading(false);
      return;
    }
    Alert.alert('Sign Out', 'Are you sure?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Sign Out', style: 'destructive', onPress: async () => { setLoading(true); await signOut(); setLoading(false); } },
    ]);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    if (refreshProfile) await refreshProfile();
    await Promise.all([loadUnreadCount(), loadWorkoutHistory(), loadPRs(), loadAchievements()]);
    setRefreshing(false);
  };

  if (loadingProfile) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
        <SkeletonRow rows={6} />
      </SafeAreaView>
    );
  }

  if (!profile) {
    return (
      <SafeAreaView style={[styles.center, { backgroundColor: c.background }]} edges={['top']}>
        <Text style={{ color: c.textSecondary, fontSize: 16 }}>Profile not found</Text>
      </SafeAreaView>
    );
  }

  const tierLabel = ({ free: 'BASE', pro: 'PRO', coach_pro: 'COACH', org_pro: 'CLUB' } as Record<string, string>)[tier] ?? 'BASE';
  const isBase = tierLabel === 'BASE';

  return (
    <SafeAreaView style={{ backgroundColor: c.background, flex: 1 }} edges={['top']}>
      <Animated.View style={{ flex: 1, opacity: fadeAnim }}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.accent} />}
      >
        {/* ── Header Actions ── */}
        {!isViewingOtherUser ? (
          <View style={styles.headerActions}>
            <TouchableOpacity
              style={styles.headerBtn}
              onPress={() => router.push('/(tabs)/notifications')}
              accessibilityLabel={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
            >
              <Bell size={22} color={c.text} />
              {unreadCount > 0 && (
                <View style={[styles.badge, { backgroundColor: c.error }]}>
                  <Text style={styles.badgeText}>{unreadCount > 9 ? '9+' : unreadCount}</Text>
                </View>
              )}
            </TouchableOpacity>
            <TouchableOpacity style={styles.headerBtn} onPress={() => router.push('/settings')} accessibilityRole="button" accessibilityLabel="Settings">
              <Settings size={22} color={c.text} />
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.headerActions}>
            <TouchableOpacity
              style={styles.headerBtn}
              onPress={() => setShowBlockReport(true)}
              accessibilityRole="button"
              accessibilityLabel="More options"
            >
              <MoreHorizontal size={22} color={c.text} />
            </TouchableOpacity>
          </View>
        )}

        {/* ── Identity ── */}
        <View style={styles.identity}>
          <TouchableOpacity
            style={[styles.avatar, { backgroundColor: c.surfaceVariant, borderColor: c.border }]}
            onPress={() => !isViewingOtherUser && handleAvatarUpload()}
            disabled={isViewingOtherUser || avatarUploading}
            activeOpacity={0.7}
          >
            {avatarUploading ? (
              <ActivityIndicator color={c.accent} />
            ) : profile?.avatar_url ? (
              <Image
                key={profile.avatar_url}
                source={{ uri: profile.avatar_url }}
                style={styles.avatarImage}
                accessibilityLabel={`${displayName}'s avatar`}
                onError={() => {}}
              />
            ) : (
              <Text style={[styles.avatarText, { color: c.text }]}>{initial}</Text>
            )}
            {!isViewingOtherUser && !avatarUploading && (
              <View style={[styles.cameraCircle, { backgroundColor: c.accent }]}>
                <Camera size={12} color={c.accentForeground} />
              </View>
            )}
          </TouchableOpacity>

          <View style={styles.nameRow}>
            <Text style={[styles.name, { color: c.text }]} accessibilityRole="header">{displayName}</Text>
            {!isViewingOtherUser && (
              <View style={[styles.tierChip, {
                backgroundColor: isBase ? 'transparent' : c.accent + '20',
                borderColor: isBase ? c.textTertiary : c.accent,
              }]}>
                <Text style={[styles.tierChipText, { color: isBase ? c.textTertiary : c.accent }]}>
                  {tierLabel}
                </Text>
              </View>
            )}
            <FounderBadge createdAt={profile?.created_at} />
          </View>
          <Text style={[styles.handle, { color: c.textTertiary }]}>@{handle}</Text>
          {bio ? <Text style={[styles.bio, { color: c.textSecondary }]}>{bio}</Text> : null}

          {/* Gym chips — show formal memberships OR home_gym_name if no memberships */}
          {userGyms.length === 0 && (profile as any)?.home_gym_name && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 8, justifyContent: 'center' }}>
              <MapPin size={12} color={c.textTertiary} />
              <Text style={{ fontSize: 12, fontFamily: TYPO.body.semibold, color: c.textSecondary }}>
                {(profile as any).home_gym_name}
              </Text>
            </View>
          )}
          {userGyms.length > 0 && (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 6, marginTop: 8 }}>
              {userGyms.map((g) => {
                const isPrimary = profile?.gym_id === g.gym_id;
                return (
                  <View
                    key={g.gym_id}
                    style={{
                      flexDirection: 'row', alignItems: 'center', gap: 4,
                      backgroundColor: isPrimary ? c.accent + '20' : c.surfaceVariant,
                      paddingHorizontal: 10, paddingVertical: 4, borderRadius: 2,
                      borderWidth: isPrimary ? 1 : 0, borderColor: c.accent,
                    }}
                  >
                    <MapPin size={12} color={isPrimary ? c.accent : c.textTertiary} />
                    <Text style={{ fontSize: 12, fontFamily: TYPO.body.semibold, color: isPrimary ? c.accent : c.textSecondary }}>
                      {g.gym_name}
                    </Text>
                  </View>
                );
              })}
            </View>
          )}

          {/* Quick stats row */}
          <View style={styles.statsRow}>
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: c.text }]}>{totalWorkouts}</Text>
              <Text style={[styles.statLabel, { color: c.textTertiary }]}>Workouts</Text>
              {totalWorkouts > 0 && totalWorkouts < 10 && (
                <Text style={[styles.statContext, { color: c.textTertiary }]}>Great start! 💪</Text>
              )}
              {totalWorkouts >= 10 && totalWorkouts < 50 && (
                <Text style={[styles.statContext, { color: c.textTertiary }]}>Building momentum!</Text>
              )}
              {totalWorkouts >= 50 && (
                <Text style={[styles.statContext, { color: c.accent }]}>Beast mode! 🔥</Text>
              )}
            </View>
            <View style={[styles.statDivider, { backgroundColor: c.border }]} />
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: c.text }]}>{duelScore || '—'}</Text>
              <Text style={[styles.statLabel, { color: c.textTertiary }]}>Strength</Text>
            </View>
            <View style={[styles.statDivider, { backgroundColor: c.border }]} />
            <View style={styles.statItem}>
              <DuelWinLossRecord userId={profile.id} variant="compact" />
              <Text style={[styles.statLabel, { color: c.textTertiary }]}>Record</Text>
            </View>
          </View>

          {/* Lifetime volume */}
          {lifetimeVolume > 0 && (
            <View style={[styles.statItem, { alignItems: 'center', paddingVertical: 8, borderTopWidth: 1, borderTopColor: c.border }]}>
              <Text style={[styles.statValue, { color: c.text, fontSize: 20 }]}>
                {lifetimeVolume >= 1000000
                  ? `${(lifetimeVolume / 1000000).toFixed(1)}M`
                  : lifetimeVolume >= 1000
                  ? `${Math.round(lifetimeVolume / 1000)}K`
                  : lifetimeVolume.toLocaleString()} {userUnits}
              </Text>
              <Text style={[styles.statLabel, { color: c.textTertiary }]}>Lifetime Volume</Text>
            </View>
          )}

          {/* Action buttons */}
          <View style={styles.actionRow}>
            {!isViewingOtherUser && (
              <>
                <TouchableOpacity
                  style={[styles.actionBtn, { borderColor: c.border }]}
                  onPress={() => router.push('/(tabs)/edit-profile')}
                  accessibilityRole="button"
                  accessibilityLabel="Edit Profile"
                >
                  <Edit size={14} color={c.textSecondary} />
                  <Text style={[styles.actionBtnText, { color: c.textSecondary }]}>Edit Profile</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.actionBtn, { borderColor: c.border }]}
                  accessibilityRole="button"
                  accessibilityLabel="Share Profile"
                  onPress={async () => {
                    const link = generateProfileShareLink(handle);
                    const deepLink = `duel://u/${encodeURIComponent(handle)}`;
                    try {
                      if (Platform.OS === 'web') {
                        if (typeof navigator !== 'undefined' && navigator.share) {
                          await navigator.share({ title: 'DUEL', text: `Check out my profile on DUEL! ${link}`, url: link });
                        } else {
                          await navigator.clipboard.writeText(link);
                          if (typeof window !== 'undefined') window.alert('Profile link copied to clipboard!');
                        }
                      } else {
                        const { Share: RNShare } = require('react-native');
                        await RNShare.share({ message: `Check out my profile on DUEL! ${link}`, url: link });
                      }
                    } catch { /* user cancelled */ }
                  }}
                >
                  <Share2 size={14} color={c.textSecondary} />
                  <Text style={[styles.actionBtnText, { color: c.textSecondary }]}>Share</Text>
                </TouchableOpacity>
              </>
            )}
            {isViewingOtherUser && isVerifiedTrainer && !loadingTrainerInfo && (() => {
              const ctaConfig = getCtaForStatus(trainerLinkStatus, false);
              return (
                <TouchableOpacity
                  style={[styles.trainerBtn, { backgroundColor: ctaConfig.variant === 'success' ? c.success : c.accent }]}
                  onPress={() => !ctaConfig.disabled && handleRequestTrainer()}
                  disabled={loading || ctaConfig.disabled}
                >
                  <Users size={14} color={c.accentForeground} />
                  <Text style={[styles.trainerBtnText, { color: c.accentForeground }]}>{ctaConfig.label} as Trainer</Text>
                </TouchableOpacity>
              );
            })()}
          </View>
        </View>

        {/* ── Rank & Freshness ── */}
        {profile.current_rank && (
          <TouchableOpacity
            onPress={() => setShowRankTierInfo(true)}
            style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}
            activeOpacity={0.7}
          >
            <View style={styles.cardRow}>
              <Crown size={16} color={c.accent} />
              <Text style={[styles.cardRowLabel, { color: c.text }]}>Rank</Text>
              <Text style={[styles.cardRowValue, { color: c.accent }]}>#{profile.current_rank}</Text>
              <ChevronRight size={14} color={c.textTertiary} />
            </View>
          </TouchableOpacity>
        )}

        <RankTierInfo visible={showRankTierInfo} onClose={() => setShowRankTierInfo(false)} />

        {/* PR Showcase removed — PRs accessible via Analytics tab */}

        {/* ── Follow + Challenge buttons (other user) ── */}
        {isViewingOtherUser && profile?.id && (
          <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 10, marginHorizontal: SPACE.md, marginBottom: SPACE.md }}>
            <FollowButton targetUserId={profile.id} />
            <TouchableOpacity
              style={[styles.actionBtn, { borderColor: c.accent, backgroundColor: c.accent }]}
              onPress={() => router.push({ pathname: '/(tabs)/duels', params: { challengeUserId: profile.id } } as any)}
              accessibilityRole="button"
              accessibilityLabel="Challenge to Duel"
            >
              <Swords size={14} color="#000" />
              <Text style={[styles.actionBtnText, { color: '#000' }]}>Challenge to Duel</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* ── Activity Heatmap ── */}
        {workoutDates.length > 0 && (
          <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
            <View style={styles.cardHeader}>
              <Flame size={16} color={c.accent} />
              <Text style={[styles.cardTitle, { color: c.accent }]}>ACTIVITY</Text>
              {memberSince && (
                <Text style={[styles.memberSince, { color: c.textTertiary }]}>Since {memberSince}</Text>
              )}
            </View>
            <ActivityHeatmap workoutDates={workoutDates} weeks={12} />
            {!isViewingOtherUser && (
              <TouchableOpacity
                style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingTop: 10, gap: 4 }}
                onPress={() => router.push('/workout-calendar' as any)}
              >
                <Calendar size={14} color={c.accent} />
                <Text style={{ fontFamily: 'Inter_600SemiBold', fontSize: 13, color: c.accent }}>
                  View Calendar
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* PR Progression + Training Split moved to Analytics tab */}

        {/* ── Achievements ── */}
        {achievements.length > 0 && (
          <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
            <View style={styles.cardHeader}>
              <Award size={16} color={c.accent} />
              <Text style={[styles.cardTitle, { color: c.accent }]}>ACHIEVEMENTS</Text>
              <Text style={[styles.seeAll, { color: c.accent }]}>
                {achievements.filter(a => a.unlocked).length}/{achievements.length}
              </Text>
            </View>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ gap: 8, paddingVertical: 4 }}
            >
              {achievements.map((a) => (
                <AchievementBadge key={a.id} achievement={a} size="normal" />
              ))}
            </ScrollView>
            <TouchableOpacity
              onPress={() => router.push('/badges' as any)}
              style={{ paddingVertical: 8, alignItems: 'center' }}>
              <Text style={{ color: c.accent, fontSize: 13, fontWeight: '600' }}>
                View all {achievements.length} badges →
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* ── Elo Rating ── */}
        {profile?.id && <EloRatingCard profileId={profile.id} showCoachInsights={hasAtLeast(tier, 'coach_pro') && isViewingOtherUser} />}

        {/* ── Career Stats ── */}
        {profile?.id && <CareerStatsCard profileId={profile.id} />}

        {/* ── PR Timeline ── */}
        {profile?.id && <PRTimeline profileId={profile.id} />}

        {/* ── Badges (server-backed) ── */}
        {profile?.id && (
          <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
            <BadgeGrid profileId={profile.id} />
          </View>
        )}

        {/* ── Navigation Sections ── */}
        {!isViewingOtherUser && (
          <>
            <View style={styles.section}>
              <Text style={[styles.sectionLabel, { color: c.accent }]} accessibilityRole="header">MY LIFTING</Text>
              <View style={[styles.menuGroup, { backgroundColor: c.surface, borderColor: c.border }]}>
                <MenuItem icon={Trophy} label="My PRs" color={c} onPress={() => router.push('/my-prs')} />
                <MenuDivider color={c.border} />
                <MenuItem icon={Activity} label="Workout History" color={c} onPress={() => router.push('/(tabs)/record?view=logs')} />
                <MenuDivider color={c.border} />
                <MenuItem icon={Swords} label="Duel History" color={c} onPress={() => router.push('/(tabs)/duels' as any)} />
              </View>
            </View>

            <View style={[styles.section, { marginBottom: 20 }]}>
              <Text style={[styles.sectionLabel, { color: c.accent }]} accessibilityRole="header">ACCOUNT</Text>
              <View style={[styles.menuGroup, { backgroundColor: c.surface, borderColor: c.border }]}>
                <MenuItem icon={Edit} label="Edit Profile" color={c} onPress={() => router.push('/(tabs)/edit-profile')} />
                <MenuDivider color={c.border} />
                <MenuItem icon={Settings} label="Settings" color={c} onPress={() => router.push('/settings')} />
              </View>
            </View>

        {/* Founder System — hidden, activation-ready */}
        {isFounderSystemVisible({ email: user?.email }) && (
          <View style={{ marginTop: 24, paddingHorizontal: 16 }}>
            <Text style={{ fontSize: 11, letterSpacing: 1.5, color: theme.colors.textSecondary, marginBottom: 12, fontFamily: 'Inter_700Bold' }}>
              FOUNDER STATUS
            </Text>
            {isFoundingAthlete(profile?.created_at) && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Text style={{ fontSize: 16 }}>🏛️</Text>
                <Text style={{ color: theme.colors.text, fontFamily: 'Inter_700Bold', fontSize: 14 }}>Founding Athlete</Text>
              </View>
            )}
            <SummitProgressCard
              status={computeSummitStatus({ rank: 1, totalUsers: 100, score: 0 })}
              founderScore={computeFounderScore({ strengthScore: profile?.duel_standard_strength_kg ?? 0 }).score}
            />
          </View>
        )}

            <TouchableOpacity
              style={[styles.signOut, { borderColor: c.border }]}
              onPress={handleSignOut}
              disabled={loading}
              accessibilityRole="button"
              accessibilityLabel="Sign Out"
            >
              <LogOut size={16} color={c.textTertiary} />
              <Text style={[styles.signOutText, { color: c.textTertiary }]}>Sign Out</Text>
            </TouchableOpacity>
          </>
        )}

        <View style={{ height: 60 }} />
      </ScrollView>
      </Animated.View>
      <TutorialOverlay screenKey="profile" />
      {isViewingOtherUser && profile?.id && (
        <BlockReportModal
          visible={showBlockReport}
          onClose={() => setShowBlockReport(false)}
          targetUserId={profile.id}
          targetDisplayName={displayName}
        />
      )}
    </SafeAreaView>
  );
}

function MenuItem({ icon: Icon, label, color, onPress }: { icon: any; label: string; color: any; onPress: () => void }) {
  return (
    <TouchableOpacity style={styles.menuItem} onPress={onPress} accessibilityRole="button" accessibilityLabel={label}>
      <Icon size={17} color={color.textTertiary} />
      <Text style={[styles.menuLabel, { color: color.text }]}>{label}</Text>
      <ChevronRight size={15} color={color.textTertiary} />
    </TouchableOpacity>
  );
}

function MenuDivider({ color }: { color: string }) {
  return <View style={[styles.menuDivider, { backgroundColor: color }]} />;
}

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  content: { paddingBottom: 60 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },

  // Header actions
  headerActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: SPACE.sm,
    paddingHorizontal: SPACE.md + SPACE.xs,
    paddingTop: SPACE.sm,
  },
  headerBtn: { padding: 6, position: 'relative' },
  badge: { position: 'absolute', top: 2, right: 2, borderRadius: 2, minWidth: 18, height: 18, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 3 },
  badgeText: { color: '#fff', fontSize: 10, fontWeight: '700' },

  // Identity
  identity: { alignItems: 'center', paddingTop: SPACE.sm, paddingBottom: SPACE.lg, paddingHorizontal: SPACE.md + SPACE.xs },
  avatar: { width: 90, height: 90, borderRadius: RADIUS.sm, justifyContent: 'center', alignItems: 'center', borderWidth: 1, marginBottom: 12, position: 'relative', overflow: 'visible' },
  avatarImage: { width: 88, height: 88, borderRadius: RADIUS.xs },
  avatarText: { fontSize: 30, fontFamily: TYPO.heading.bold },
  cameraCircle: { position: 'absolute', bottom: -2, right: -2, width: 26, height: 26, borderRadius: 4, alignItems: 'center', justifyContent: 'center' },
  nameRow: { flexDirection: 'row', alignItems: 'center', gap: SPACE.sm },
  name: { fontSize: 22, fontFamily: TYPO.heading.bold, textTransform: 'uppercase', letterSpacing: TRACKING.normal },
  tierChip: { borderWidth: 1, paddingVertical: 2, paddingHorizontal: SPACE.sm, borderRadius: RADIUS.xs },
  tierChipText: { fontSize: 10, fontFamily: TYPO.subheading.extrabold, letterSpacing: 1 },
  handle: { fontSize: 14, fontFamily: TYPO.body.medium, marginTop: 2 },
  bio: { fontSize: 13, fontFamily: TYPO.body.regular, textAlign: 'center', marginTop: 8, lineHeight: 18, paddingHorizontal: 24 },

  // Stats row
  statsRow: { flexDirection: 'row', alignItems: 'center', marginTop: SPACE.md, gap: SPACE.sm },
  statItem: { alignItems: 'center', flex: 1 },
  statValue: { fontSize: 18, fontFamily: TYPO.heading.bold },
  statLabel: { fontSize: 11, fontFamily: TYPO.subheading.bold, marginTop: 2, textTransform: 'uppercase', letterSpacing: TRACKING.wider },
  statContext: { fontSize: 10, fontFamily: TYPO.body.medium, marginTop: 2 },
  statDivider: { width: 1, height: 28 },

  // Action row
  actionRow: { flexDirection: 'row', gap: SPACE.sm + 2, marginTop: SPACE.base + 2 },
  actionBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: SPACE.sm, paddingHorizontal: SPACE.md, borderRadius: RADIUS.none, borderWidth: 1 },
  actionBtnText: { fontSize: 13, fontFamily: TYPO.heading.bold, textTransform: 'uppercase', letterSpacing: TRACKING.normal },
  trainerBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: SPACE.sm, paddingHorizontal: SPACE.md, borderRadius: RADIUS.none },
  trainerBtnText: { fontSize: 13, fontFamily: TYPO.heading.bold, textTransform: 'uppercase', letterSpacing: TRACKING.normal },

  // Cards
  card: { borderWidth: 1, borderRadius: RADIUS.xs, padding: SPACE.lg, marginHorizontal: SPACE.md, marginBottom: SPACE.md, gap: SPACE.md },
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: SPACE.sm },
  cardTitle: { fontSize: FONT.xs, fontFamily: TYPO.subheading.bold, letterSpacing: TRACKING.widest, textTransform: 'uppercase', flex: 1 },
  seeAll: { fontSize: FONT.sm, fontFamily: TYPO.subheading.bold, textTransform: 'uppercase' },
  cardRow: { flexDirection: 'row', alignItems: 'center', gap: SPACE.sm },
  cardRowLabel: { fontSize: FONT.base, fontFamily: TYPO.body.semibold, flex: 1 },
  cardRowValue: { fontSize: FONT.md, fontFamily: TYPO.heading.bold },
  memberSince: { fontSize: FONT.xs, fontFamily: TYPO.body.medium },

  // PRs
  prRow: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8, borderBottomWidth: StyleSheet.hairlineWidth },
  prLift: { fontSize: 14, fontFamily: TYPO.body.semibold },
  prWeight: { fontSize: 14, fontFamily: TYPO.body.bold },
  prDate: { fontSize: FONT.xs, fontFamily: TYPO.body.medium, marginTop: 1 },

  // Sections
  section: { marginHorizontal: SPACE.md, marginBottom: SPACE.md + SPACE.xs, marginTop: SPACE.xs },
  sectionLabel: { fontSize: FONT.xs, fontFamily: TYPO.subheading.bold, letterSpacing: TRACKING.widest, textTransform: 'uppercase', marginBottom: 6, paddingLeft: 4 },
  menuGroup: { borderWidth: 1, borderRadius: RADIUS.xs, overflow: 'hidden' },
  menuItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: SPACE.md, paddingHorizontal: SPACE.md, gap: SPACE.base },
  menuLabel: { flex: 1, fontSize: FONT.base, fontFamily: TYPO.body.semibold },
  menuDivider: { height: StyleSheet.hairlineWidth, marginLeft: 45 },

  // Training split
  splitRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 },
  splitLabel: { width: 44, fontSize: FONT.xs, fontFamily: TYPO.subheading.bold, letterSpacing: 0.3 },
  splitBarTrack: { flex: 1, height: 8, borderRadius: 4, backgroundColor: 'rgba(128,128,128,0.15)', overflow: 'hidden' as const },
  splitBarFill: { height: '100%' as any, borderRadius: 4 },
  splitPct: { width: 32, fontSize: FONT.xs, fontFamily: TYPO.subheading.bold, textAlign: 'right' as const },

  // Sign out
  signOut: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: SPACE.sm, paddingVertical: SPACE.base, marginHorizontal: SPACE.md, marginTop: SPACE.sm },
  signOutText: { fontSize: FONT.base, fontFamily: TYPO.body.semibold },
});
